import React, { useState, useEffect } from 'react';
import {
  Menu, X, Phone, Stethoscope, ChevronDown, MapPin, ArrowRight, Search,
  GraduationCap, Thermometer, Plane, Handshake, Train, Award, ClipboardList,
  Medal, CalendarDays, Flag, Calendar, Newspaper, Users, Home as HomeIcon, ShieldCheck,
  HelpCircle, Camera,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { universities } from '../data/mock';
import { mainNav } from '../data/navigation';
import { Button } from './ui/button';

const iconMap = {
  GraduationCap, Thermometer, Plane, Handshake, Train, Award,
  ClipboardList, Medal, CalendarDays, Flag, Calendar, Newspaper, Users, HomeIcon, ShieldCheck,
  HelpCircle, Camera,
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeDrop, setActiveDrop] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMobileSubOpen(null); }, [location.pathname]);

  return (
    <header className={`sticky top-0 z-40 transition-shadow duration-300 bg-white ${scrolled ? 'shadow-md' : 'shadow-sm border-b border-border'}`}>
      <div className="container mx-auto max-w-7xl px-4 md:px-8 flex items-center justify-between h-[80px]">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <div className="w-12 h-12 rounded-full bg-white ring-2 ring-primary/15 shadow-md flex items-center justify-center overflow-hidden">
            <img src="/logo.jpg" alt="Russian Education" className="w-full h-full object-cover" />
          </div>
          <div className="leading-[1.05]">
            <div className="font-serif text-lg md:text-xl font-bold tracking-tight text-primary">Russian</div>
            <div className="font-serif text-lg md:text-xl font-bold tracking-tight text-brand -mt-0.5">Education</div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mt-0.5">MBBS in Russia</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-0.5">
          {mainNav.map((l) => {
            if (l.special === 'universities') return <UniversitiesDropdown key={l.label} onOpen={setActiveDrop} isOpen={activeDrop === 'universities'} />;
            if (l.children) return <MegaDropdown key={l.label} label={l.label} href={l.href} items={l.children} isOpen={activeDrop === l.label} onOpen={setActiveDrop} />;
            const active = location.pathname === l.href;
            return (
              <Link key={l.label} to={l.href}
                onMouseEnter={() => setActiveDrop(null)}
                className={`relative px-3 py-2 text-[13px] font-semibold transition-colors
                          ${active ? 'text-brand' : 'text-foreground/80 hover:text-brand'}
                          after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-[2px] after:bg-brand
                          after:origin-left after:transition-transform after:duration-300
                          ${active ? 'after:scale-x-100' : 'after:scale-x-0 hover:after:scale-x-100'}`}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        <a href="tel:+919509406802" className="hidden lg:flex items-center gap-2.5 bg-brand hover:bg-brand-dark text-white pl-3 pr-5 py-2 rounded-full shadow-lg shadow-brand/25 btn-shift transition-colors group">
          <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
            <Phone className="w-3.5 h-3.5" strokeWidth={2.2} />
          </div>
          <div className="leading-tight text-left">
            <div className="text-[9px] uppercase tracking-widest opacity-90 font-semibold">Call Now</div>
            <div className="font-serif text-sm font-bold">+91 95094 06802</div>
          </div>
        </a>

        <button className="xl:hidden text-primary" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden bg-white border-t border-border max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {mainNav.map((l) => {
              if (l.special === 'universities') {
                const isSubOpen = mobileSubOpen === 'universities';
                return (
                  <div key={l.label}>
                    <button onClick={() => setMobileSubOpen(isSubOpen ? null : 'universities')}
                      className="w-full py-2.5 px-2 text-foreground font-semibold border-b border-border flex items-center justify-between">
                      Universities ({universities.length}) <ChevronDown className={`w-4 h-4 transition-transform ${isSubOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isSubOpen && (
                      <div className="pl-3 py-2 flex flex-col gap-0.5 bg-muted rounded-lg my-1 max-h-[280px] overflow-y-auto">
                        <Link to="/universities" className="py-2 px-2 text-brand text-sm font-bold">View All Universities →</Link>
                        {universities.map((u) => (
                          <Link key={u.slug} to={`/universities/${u.slug}`} className="py-1.5 px-2 text-sm text-foreground hover:text-brand flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center text-[10px] font-bold shrink-0">{u.logo}</span>
                            <span className="truncate">{u.short} · {u.city}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (l.children) {
                const isSubOpen = mobileSubOpen === l.label;
                return (
                  <div key={l.label}>
                    <button onClick={() => setMobileSubOpen(isSubOpen ? null : l.label)}
                      className="w-full py-2.5 px-2 text-foreground font-semibold border-b border-border flex items-center justify-between">
                      {l.label} <ChevronDown className={`w-4 h-4 transition-transform ${isSubOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isSubOpen && (
                      <div className="pl-3 py-2 flex flex-col gap-0.5 bg-muted rounded-lg my-1">
                        <Link to={l.href} className="py-2 px-2 text-brand text-sm font-bold">Explore {l.label} →</Link>
                        {l.children.map((c) => {
                          const Icon = iconMap[c.icon];
                          return (
                            <Link key={c.href} to={c.href} className="py-2 px-2 text-sm text-foreground hover:text-brand flex items-start gap-2">
                              {Icon && <Icon className="w-4 h-4 mt-0.5 text-brand shrink-0" />}
                              <span>{c.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link key={l.label} to={l.href} onClick={() => setOpen(false)}
                  className="py-2.5 px-2 text-foreground font-semibold border-b border-border">{l.label}</Link>
              );
            })}
            <a href="tel:+919509406802" className="mt-4 flex items-center gap-3 bg-brand text-white p-3 rounded-xl">
              <Phone className="w-5 h-5" />
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold">Call Now</div>
                <div className="font-serif text-base font-bold">+91 95094 06802</div>
              </div>
            </a>
            <a href="https://wa.me/919509406802" target="_blank" rel="noreferrer"
              className="mt-2 text-center bg-primary text-white py-3 rounded-xl font-semibold">
              Apply Now →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function MegaDropdown({ label, href, items, isOpen, onOpen }) {
  // Wide 2-col right-anchored panel only for very large dropdowns (currently just "More").
  // Left-anchored narrow panel for everything else — prevents left-side triggers
  // (MBBS in Russia, Exams, Services) from clipping off-screen.
  const isWide = items.length > 7;
  return (
    <div className="relative"
      onMouseEnter={() => onOpen(label)}
      onMouseLeave={() => onOpen(null)}>
      <Link to={href}
        className="relative px-3 py-2 text-[13px] font-semibold text-foreground/80 hover:text-brand transition-colors flex items-center gap-1">
        {label} <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Link>

      {isOpen && (
        <div className={`absolute top-full pt-3 ${isWide ? 'right-0 w-[720px]' : 'left-0 w-[560px]'}`}>
          <div className="bg-white rounded-2xl shadow-2xl border border-border overflow-hidden">
            <div className="px-5 py-3 bg-primary text-white flex items-center justify-between">
              <div className="font-serif font-bold">{label}</div>
              <Link to={href} className="text-xs font-bold text-white hover:text-brand flex items-center gap-1 group">
                View All <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className={`p-3 grid ${isWide ? 'grid-cols-2' : 'grid-cols-1'} gap-1`}>
              {items.map((c) => {
                const Icon = iconMap[c.icon];
                return (
                  <Link key={c.href + c.label} to={c.href}
                    className="group flex items-start gap-3 p-3 rounded-lg hover:bg-brand/5 transition-colors">
                    {Icon && (
                      <div className="w-10 h-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0 group-hover:bg-brand group-hover:text-white transition-colors">
                        <Icon className="w-5 h-5" />
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-bold text-primary group-hover:text-brand transition-colors">{c.label}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{c.desc}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UniversitiesDropdown({ isOpen, onOpen }) {
  const [query, setQuery] = useState('');

  const filtered = query
    ? universities.filter((u) =>
        u.name.toLowerCase().includes(query.toLowerCase()) ||
        u.city.toLowerCase().includes(query.toLowerCase()) ||
        u.short.toLowerCase().includes(query.toLowerCase())
      )
    : universities;

  return (
    <div className="relative"
      onMouseEnter={() => onOpen('universities')}
      onMouseLeave={() => { onOpen(null); setQuery(''); }}>
      <Link to="/universities"
        className="relative px-3 py-2 text-[13px] font-semibold text-foreground/80 hover:text-brand transition-colors flex items-center gap-1">
        Universities <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Link>

      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[860px]">
          <div className="bg-white rounded-2xl shadow-2xl border border-border overflow-hidden">
            <div className="px-6 py-4 bg-primary text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Premium Institutions</div>
                <div className="font-serif font-bold text-lg">All {universities.length} Medical Universities in Russia</div>
              </div>
              <Link to="/universities" className="text-xs font-bold text-white hover:text-brand flex items-center gap-1 group">
                View All &amp; Compare <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="px-4 pt-3 pb-2 border-b border-border/60">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search 64 universities by name or city..."
                  className="w-full h-10 pl-9 pr-3 rounded-lg bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              </div>
            </div>
            <div className="p-3 grid grid-cols-2 gap-1 max-h-[420px] overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="col-span-2 p-6 text-center text-sm text-muted-foreground">No universities match “{query}”</div>
              ) : (
                filtered.map((u) => (
                  <Link key={u.slug} to={`/universities/${u.slug}`}
                    className="group flex items-start gap-3 p-2.5 rounded-lg hover:bg-brand/5 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-brand text-white flex items-center justify-center font-serif text-sm font-bold shrink-0 group-hover:scale-105 transition-transform">{u.logo}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-bold text-primary leading-tight group-hover:text-brand transition-colors line-clamp-1">{u.short} — {u.name}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {u.city}</span>
                        <span className="font-bold text-brand">{u.fee}/yr</span>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
            <div className="px-4 py-3 border-t border-border/60 bg-muted/50 text-xs text-muted-foreground flex items-center justify-between">
              <span>Showing {filtered.length} of {universities.length} universities</span>
              <Link to="/universities" className="font-bold text-brand hover:text-brand-dark">Compare Universities →</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
